import getStorage from '../storage/index'
import store from 'store2'

const AUTO_LOGOUT_KEY = 'auth-auto-logout'

function resetLocalData () {
  this.set({
    settings: getStorage(this.get('user'), 'setting')
  })

  // Propagate theme change event
  this.get('settings').on('change:theme', (model, theme) => {
    this.trigger('change:theme', model, theme)
  })

  this.trigger('change:theme', this.get('settings'), this.get('settings').get('theme'))
}

/**
 * Tracks application state
 *
 * @event change:theme Theme change
 *  @property {String} name Theme name
 *
 * @event session:start User logged in and model hydrated
 *  @property {Model} user User model
 *
 * @event session:logout User logged out
 * @event session:anonymous No user on startup
 *
 */
export default class State extends Backbone.Model {
  get defaults () {
    return {
      role: 'auth',
      page: null,
      settings: null,
      user: null,
      participant: null,
      session: null,
      // general app ready state
      ready: false
    }
  }

  get role () {
    return this.get('role')
  }

  get defaultPage () {
    // The main app `session:logout` handler resets the state and takes you to the default page
    // using `store.page` here allows us to 'remember' we should be on the complete page
    // after logging out - as `this` state object will be destroyed, and a new one made
    const autoLogoutOccurred = store.page.get(AUTO_LOGOUT_KEY)
    if (autoLogoutOccurred) {
      return 'out_of_band_complete'
    } else {
      return 'please_wait'
    }
  }

  get page () {
    return this.get('page')
  }

  get user () {
    return this.get('user')
  }

  get participant () {
    return this.get('participant')
  }

  get session () {
    return this.get('session')
  }

  get loggedIn () {
    return !!this.get('user').get('id')
  }

  initialize (attrs = {}, options = {}) {
    // Track which page change this is so we can ignore some async actions
    this.changePageCounter = 0

    // Session is fully loaded with user
    this.on('session:start session:anonymous', () => {
      resetLocalData.call(this)

      // Session started. Set app to ready state
      if (this.get('participant').get('must_consent')) {
        // check whether we should show the terms page first
        this.set({
          ready: true,
          page: 'terms_agreement'
        })
      } else {
        this.set({ ready: true })
      }
    })

    this.on('session:logout', () => {
      this.changePage(this.defaultPage)
    })

    // Nicety
    this.get('session').on('session:stopped', () => {
      this.trigger('session:logout')
    })

    this.get('session').on('session:started', () => {
      // When the page changes, we're assuming the app is ready
      // Session started. Set app to ready state
      this.set({ ready: true })

      // Session start could have an anonymous user
      if (this.get('user').id) {
        this.trigger('session:start', this.get('user'))
      } else {
        this.trigger('session:anonymous')
      }
    })
  }

  changePage (page) {
    this.set('page', page)
  }

  autoLogout () {
    // Store that this was an auto logout in `store.page` for the reset state object to see
    store.page.set(AUTO_LOGOUT_KEY, new Date())
    this.session?.destroy()
  }

  inspect () {
    const result = super.toJSON()

    // Deal with state model attributes whose values are complex types
    result.participant = this.get('participant')?.toJSON()
    result.settings = this.get('settings')?.toJSON()
    result.user = this.get('user')?.toJSON().user

    // session only has references to the participant and user, which we otherwise have anyway
    delete result.session

    return result
  }
}
